import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { useI18n } from 'vue-i18n';
import { getTemplateShareText } from '~/api';
import IconPark from '~/components/iconPark/index.vue';
import { Share } from './share';
import { ShareLink } from './shareLink';

export const TemplateShare = defineComponent({
  props: {
    isCard: {
      type: Boolean,
      default: false,
    },
    linkUrl: {
      type: String,
      default: '',
    },
    hasShare: {
      type: Boolean,
      default: true,
    },
    trackParams: {
      type: Object,
      default: () => ({}),
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
    showExtra: {
      type: Boolean,
      default: false,
    },
    templateId: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    shareToOuterUser: {
      type: Boolean,
      default: true,
    },
    shareToInnerUser: {
      type: Boolean,
      default: true,
    },
    iconClass: {
      type: String,
      require: false,
    },
    eventName: {
      type: String as PropType<TRACK_EVENT>,
      require: false,
    },
    disabledText: {
      type: String,
      require: false,
    },
    type: {
      type: String as PropType<'default' | 'shadow' | 'button'>,
      default: 'default',
    },
    size: {
      type: Number,
    },
    overlayClassName: {
      type: String,
      default: '',
    },
  },
  emits: ['openChange'],
  setup(props, { expose, emit }) {
    const { t: $t } = useI18n();

    const shareVisible = useShare();
    const slots = useSlots();
    const loading = ref(false);

    const params = {
      ...props.params,
    };

    let shareText: string = '';
    const show = ref(false);
    const onOpenChange = async (visible: boolean) => {
      if (props.disabled) {
        return;
      }
      show.value = visible;
      shareVisible.value = visible;
      emit('openChange', visible);

      if (props.templateId && visible) {
        loading.value = true;
        const res = await getTemplateShareText({
          template_id: props.templateId,
        });
        if (res?.data?.length) {
          shareText = `${res.data?.[0] || ''} ${props.linkUrl} ${res.data?.[1] || ''}`;
        }
        loading.value = false;
      }
    };

    watch(
      () => shareVisible.value,
      (cur, before) => {
        if (cur !== before && !cur) {
          show.value = false;
        }
      },
    );

    const eventName = props.eventName || TRACK_EVENT.YOUYAN_PLATFORM_SHARE;

    function getIcon() {
      let displayIcon = (
        <div class="flex items-center justify-center h-[28px] w-[28px] rounded-full text-white bg-[rgba(0,0,0,0.4)] hover:bg-[rgba(0,0,0,0.6)]">
          <IconPark name="arrow-share" size={props.size || '14'} class={`${props.iconClass}`} />
        </div>
      );
      // todo: 这里后续优化下写法
      if (props.type === 'shadow') {
        displayIcon = (
          <div class="flex items-center justify-center p-[10px] rounded-[8px] bg-back-0 hover:bg-back-3 cursor-pointer">
            <IconPark name="arrow-share" size={props.size || '16'} class={`${props.iconClass}`} />
          </div>
        );
      }

      if (props.type === 'button') {
        displayIcon = (
          <div class="grid place-content-center !w-[40px] !h-[40px] border-[1px] border-solid border-back-5 !rounded-[8px] hover:!bg-back-3">
            <IconPark name="arrow-share" size={props.size || '16'} class={`${props.iconClass}`} />
          </div>
        );
      }

      return (
        <Tooltip content={$t('fen-xiang')} placement="top">
          {displayIcon}
        </Tooltip>
      );
    }

    const popContent = () => [
      <div
        class="text-16 pb-[10px] mb-[12px] text-center -mx-[12px] font-semibold text-font-1"
        style="border-bottom: 1px solid var(--back-4);"
      >
        {props.title || $t('fen-xiang-kong-ge') + $t('mo-ban')}
      </div>,
      <div class="w-[360px] p-[18px] pt-[0px] pb-[12px]">
        {checkPermission(ActionListKey.TEMPLATE_SHARE) && props.shareToInnerUser && (
          <Share
            params={params}
            eventName={eventName}
            height="170px"
            trackParams={props.trackParams}
            showExtra={props.showExtra}
            linkUrl={props.linkUrl}
          />
        )}
        {props.linkUrl && props.shareToOuterUser && (
          <ShareLink
            key={shareText}
            url={props.linkUrl}
            text={shareText}
            eventName={eventName}
            disabled={loading.value}
            trackParams={props.trackParams}
            onClose={!checkPermission(ActionListKey.TEMPLATE_SHARE) ? onOpenChange : () => {}}
            name={`${
              checkPermission(ActionListKey.TEMPLATE_SHARE) && props.shareToInnerUser ? '2.' : ''
            }${$t('fen-xiang-gei-wai-bu-yong-hu')}`}
          />
        )}
      </div>,
    ];

    return () => {
      const icon = getIcon();
      if (props.disabled) {
        return (
          <a-popover
            content={
              <span class="text-font-1">
                {props.disabledText || $t('shang-jia-hou-ji-ke-cao-zuo')}
              </span>
            }
          >
            <div class={[`${'!cursor-not-allowed opacity-60'}`]}>
              {slots.default ? slots.default() : icon}
            </div>
          </a-popover>
        );
      }

      return (
        <a-popover
          trigger="click"
          open={show.value}
          placement="bottomRight"
          onOpenChange={onOpenChange}
          overlayClassName={props.overlayClassName}
        >
          {{
            content: () => (slots.popContent ? slots.popContent() : popContent()),
            default: () => (slots.default ? slots.default() : icon),
          }}
        </a-popover>
      );
    };
  },
});
